import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { View, Pressable, PanResponder, useWindowDimensions, StyleSheet, Platform, Text } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Animated, { useSharedValue, useAnimatedStyle, runOnJS } from 'react-native-reanimated';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { ZoomableViewEvent } from '@openspacelabs/react-native-zoomable-view';
import { debounce } from 'lodash';

export interface BubbleData {
  _id: string;
  dataTitle: string;
  count: number;
  dataTags?: string;
  dataSchema?: any;
  x: number;
  y: number;
  radius: number;
  color: string;
  originalData: any;
}

export interface BubbleComponentProps {
  bubble: BubbleData;
  index: number;
  zoomProps: ZoomableViewEvent;
  onBubbleTap: (bubble: BubbleData) => void;
  onPositionChange: (bubbleId: string, x: number, y: number) => void;
  disableDrag?: boolean;
  debugMode?: boolean;
}

export interface BubbleComponentHandle {
  setPosition: (x: number, y: number) => void;
  getPosition: () => { x: number; y: number };
  getBubbleData: () => BubbleData;
}

const BubbleComponent = forwardRef<BubbleComponentHandle, BubbleComponentProps>((props, ref) => {
  const { bubble, index, zoomProps, onBubbleTap, onPositionChange, disableDrag = false, debugMode = false } = props;
  const theme = themeSelector();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  
  // Position state
  const [position, setPosition] = useState({ x: bubble.x, y: bubble.y });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPosition = useRef({ x: 0, y: 0 });
  
  // Animated values for smooth dragging
  const translationX = useSharedValue(bubble.x);
  const translationY = useSharedValue(bubble.y);
  const scale = useSharedValue(1);

  // Initialize position from bubble data
  useEffect(() => {
    setPosition({ x: bubble.x, y: bubble.y });
    translationX.value = bubble.x;
    translationY.value = bubble.y;
  }, [bubble.x, bubble.y]);

  // Imperative handle for external control
  useImperativeHandle(ref, () => ({
    setPosition: (x: number, y: number) => {
      setPosition({ x, y });
      translationX.value = x;
      translationY.value = y;
    },
    getPosition: () => position,
    getBubbleData: () => bubble,
  }));

  // Debounced position update callback
  const debouncedPositionUpdate = useCallback(
    debounce((x: number, y: number) => {
      onPositionChange(bubble._id, x, y);
    }, 50), // Reduced from 100ms to 50ms for more responsive updates
    [bubble._id, onPositionChange]
  );

  // Pan responder for dragging - Android-safe version
  const dragPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => !disableDrag,
    onMoveShouldSetPanResponder: (evt, gestureState) => {
      // On Android, require movement before capturing to avoid conflicts
      if (Platform.OS === 'android') {
        return !disableDrag && (Math.abs(gestureState.dx) > 5 || Math.abs(gestureState.dy) > 5);
      }
      return !disableDrag;
    },
    onPanResponderGrant: (event, gestureState) => {
      setIsDragging(true);
      scale.value = 1.1; // Slightly scale up when dragging starts
      
      // Store the starting position when drag begins
      dragStartPosition.current = { x: translationX.value, y: translationY.value };
      
      if (debugMode) {
        console.log('Bubble drag started:', bubble.dataTitle, 'from:', dragStartPosition.current);
      }
    },
    onPanResponderMove: (event, gestureState) => {
      const { dx, dy } = gestureState;
      const { zoomLevel = 1 } = zoomProps;
      
      // Adjust movement based on zoom level
      const adjustedDx = dx / zoomLevel;
      const adjustedDy = dy / zoomLevel;
      
      // Calculate new position based on drag start position + delta
      const newX = dragStartPosition.current.x + adjustedDx;
      const newY = dragStartPosition.current.y + adjustedDy;
      
      // Boundary constraints (keep within reasonable bounds)
      const constrainedX = Math.max(bubble.radius, Math.min(windowWidth - bubble.radius, newX));
      const constrainedY = Math.max(bubble.radius, Math.min(windowHeight - bubble.radius, newY));
      
      // Update animation values immediately for smooth dragging
      translationX.value = constrainedX;
      translationY.value = constrainedY;
      
      // Update position with debouncing to avoid too many calls
      debouncedPositionUpdate(constrainedX, constrainedY);
    },
    onPanResponderRelease: (event, gestureState) => {
      setIsDragging(false);
      scale.value = 1.0; // Reset scale
      
      // Update final position state
      const finalX = translationX.value;
      const finalY = translationY.value;
      setPosition({ x: finalX, y: finalY });
      
      if (debugMode) {
        console.log('Bubble drag ended:', bubble.dataTitle, 'Final position:', finalX, finalY);
      }
      
      // Final position update
      onPositionChange(bubble._id, finalX, finalY);
    },
    onPanResponderTerminationRequest: () => {
      // On Android, allow termination to prevent conflicts with zoomable view
      if (Platform.OS === 'android') {
        return true;
      }
      return false;
    }
  });

  // Tap handler for bubble interaction
  const handleBubbleTap = useCallback(() => {
    if (!isDragging) {
      onBubbleTap(bubble);
    }
  }, [bubble, isDragging, onBubbleTap]);

  // Animated style for the bubble
  const animatedStyle = useAnimatedStyle(() => {
    return {
      left: translationX.value - bubble.radius,
      top: translationY.value - bubble.radius,
      transform: [
        { scale: scale.value },
      ],
    } as any;
  });

  const bubbleStyle = {
    position: 'absolute' as const,
    width: bubble.radius * 2,
    height: bubble.radius * 2,
    borderRadius: bubble.radius,
    backgroundColor: bubble.color,
    elevation: isDragging ? 8 : 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: isDragging ? 4 : 2 },
    shadowOpacity: isDragging ? 0.4 : 0.25,
    shadowRadius: isDragging ? 6 : 3.84,
    zIndex: isDragging ? 1000 : 10,
    borderWidth: isDragging ? 2 : 0,
    borderColor: theme ? '#333' : '#fff',
  };

  const fontSize = Math.max(12, Math.min(18, bubble.radius * 0.3)); // Increased font size calculation

  return (
    <Animated.View style={[bubbleStyle, animatedStyle]}>
      {/* Drag handle - visible when dragging or in debug mode */}
      {(isDragging || debugMode) && (
        <View style={styles.dragHandle}>
          <MaterialIcons 
            name="drag-indicator" 
            size={20} 
            color={theme ? "#333" : "#fff"} 
            style={styles.dragIcon}
          />
        </View>
      )}
      
      {/* Main bubble content */}
      <View style={styles.bubbleContent}>
        <View
          {...dragPanResponder.panHandlers}
          style={{ flex: 1 }}
        >
          <Pressable
            style={styles.bubbleContent}
            onPress={handleBubbleTap}
          >
            <Text
              style={{
                fontSize,
                color: 'white',
                fontWeight: 'bold',
                textAlign: 'center',
                textShadowColor: 'rgba(0, 0, 0, 0.75)', // Add text shadow for better visibility
                textShadowOffset: { width: 1, height: 1 },
                textShadowRadius: 2,
              }}
              numberOfLines={2}
              adjustsFontSizeToFit
            >
              {bubble.dataTitle || ''}
            </Text>
            <Text style={[styles.bubbleCount, { 
              textShadowColor: 'rgba(0, 0, 0, 0.75)',
              textShadowOffset: { width: 1, height: 1 },
              textShadowRadius: 2,
            }]}>
              {String(bubble.count ?? 0)}
            </Text>
            
            {/* Debug info */}
            {debugMode && (
              <Text style={styles.debugText}>
                {String(Math.round(position.x))}, {String(Math.round(position.y))}
              </Text>
            )}
          </Pressable>
        </View>
      </View>      {/* Dragging indicator */}
      {isDragging && (
        <View style={styles.draggingIndicator}>
          <View style={styles.draggingDot} />
        </View>
      )}
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  bubbleContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
  },
  bubbleCount: {
    fontSize: 10,
    color: 'white',
    fontWeight: 'bold',
    marginTop: 4,
    textAlign: 'center',
  },
  dragHandle: {
    position: 'absolute',
    top: -10,
    right: -10,
    zIndex: 1001,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 15,
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
  },
  dragIcon: {
    opacity: 0.7,
  },
  draggingIndicator: {
    position: 'absolute',
    bottom: -15,
    left: '50%',
    transform: [{ translateX: -5 }],
    zIndex: 1001,
  },
  draggingDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FF6B6B',
    opacity: 0.8,
  },
  debugText: {
    fontSize: 8,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
    textAlign: 'center',
  },
});

export default BubbleComponent;
